// PopupModal.tsx

import * as React from 'react';
import Dialog from 'material-ui/Dialog';
import { RaisedButton } from 'material-ui';
import { Loader } from '../Loader/Loader';

interface IUserDetail {
    name: string;
    email?: string;
    assigned: number;
    completed: number;
    modules: number;
}

interface IPopupData {
    month: string;
    category: string;
    users: IUserDetail[];
    isLoading?: boolean;
    totalAssigned: number;
    totalCompleted: number;
    totalUsers: number;
    totalModules: number;
    year?: string;
}

interface IPopupModalProps {
    isOpen: boolean;
    data: IPopupData | null;
    title: string;
    onClose: () => void;
}

const PopupModal: React.FC<IPopupModalProps> = ({ isOpen, data, title, onClose }) => {
    if (!data || !isOpen) return null;

    const isLoading = data.isLoading || false;

    //  Loading state
    if (isLoading) {
        return (
            <Dialog
                title="Overall Training Statistics"
                modal={false}
                open={isOpen}
                onRequestClose={onClose}
                autoScrollBodyContent={true}
                contentStyle={{ width: '90%', maxWidth: '1200px' }}
            >
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 20px',
                    minHeight: '350px',
                    width: '100%'
                }}>
                    <Loader type="submission" />
                    <div style={{
                        marginTop: '20px',
                        color: '#666',
                        fontSize: '16px',
                        fontWeight: 500
                    }}>
                        Loading user data...
                    </div>
                </div>
            </Dialog>
        );
    }

    //  Data state
    const users = data.users || [];
    const hasUsers = users.length > 0;

    const getCompletionRate = (assigned: number, completed: number) => {
        if (assigned === 0) return 0;
        return Math.round((completed / assigned) * 100);
    };

    const getCompletionColor = (rate: number) => {
        if (rate >= 80) return '#50b806';
        if (rate >= 50) return '#ffa500';
        return '#f50b0b';
    };

    const totalRow = {
        name: '📊 TOTAL',
        assigned: users.reduce((sum, u) => sum + (u.assigned || 0), 0),
        completed: users.reduce((sum, u) => sum + (u.completed || 0), 0),
        modules: users.reduce((sum, u) => sum + (u.modules || 0), 0)
    };

    //  Format title
    const categoryName = data.category || 'All';
    const monthName = data.month || 'Unknown';
    const yearName = data.year || '';
    const formattedTitle = `UserTraining Statistics`;

    return (
        <Dialog
            title={
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: '600' }}>
                        📊 Overall Training Statistics
                    </div>
                </div>
            }
            modal={false}
            open={isOpen}
            onRequestClose={onClose}
            autoScrollBodyContent={true}
            contentStyle={{ width: '90%', maxWidth: '1200px' }}
        >
            {/*  Category & Month Info */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '20px',
                padding: '12px 16px',
                background: '#f0f4f8',
                borderRadius: '8px',
                fontSize: '14px',
                flexWrap: 'wrap',
                gap: '10px'
            }}>
                <span>📂 <strong>Category:</strong> {data.category || 'All'}</span>
                <span>📅 <strong>Month:</strong> {data.month || 'Unknown'} {data.year || ''}</span>
                <span>👥 <strong>Newly Assigned Users:</strong> {users.length}</span>
            </div>

            {/* Summary Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '15px',
                marginBottom: '20px'
            }}>
                <div style={{ background: '#2f89ea', color: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{data.totalAssigned || 0}</div>
                    <div>Training Assigned</div>
                </div>
                <div style={{ background: '#50b806', color: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{data.totalCompleted || 0}</div>
                    <div>Training Completed</div>
                </div>
                <div style={{ background: '#000000', color: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{data.totalUsers || 0}</div>
                    <div>Total Users</div>
                </div>
                <div style={{ background: '#f50b0b', color: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{data.totalModules || 0}</div>
                    <div>Total Modules</div>
                </div>
            </div>

            {/*  FORMATTED TITLE  */}
            <div style={{
                textAlign: 'center',
                padding: '10px 0 14px 0',
                borderTop: '2px solid #e5e7eb',
                borderBottom: '2px solid #e5e7eb',
                marginBottom: '16px'
            }}>
                <div style={{
                    fontSize: '17px',
                    fontWeight: '600',
                    color: '#1a1a2e',
                    letterSpacing: '0.5px'
                }}>
                    📋 {formattedTitle}
                </div>
            </div>

            {/* Users Table */}
            <div style={{ overflowX: 'auto' }}>
                {hasUsers ? (
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '14px',
                        borderRadius: '8px',
                        overflow: 'hidden'
                    }}>
                        <thead>
                            <tr style={{ backgroundColor: '#003152', color: 'white' }}>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>
                                    👤 User Name
                                </th>
                                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>
                                    📋 Training Assigned
                                </th>
                                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>
                                    ✅ Training Completed
                                </th>
                                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>
                                    📦 Modules
                                </th>
                                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>
                                    📊 Completion %
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user, index) => {
                                const rate = getCompletionRate(user.assigned || 0, user.completed || 0);
                                return (
                                    <tr
                                        key={index}
                                        style={{
                                            backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white',
                                            borderBottom: '1px solid #eee'
                                        }}
                                    >
                                        <td style={{ padding: '10px', fontWeight: '500' }}>
                                            {user.name || 'Unknown User'}
                                        </td>
                                        <td style={{ padding: '10px', textAlign: 'center' }}>
                                            {user.assigned || 0}
                                        </td>
                                        <td style={{ padding: '10px', textAlign: 'center' }}>
                                            <span style={{
                                                color: (user.completed || 0) > 0 ? '#50b806' : '#999',
                                                fontWeight: 'bold'
                                            }}>
                                                {user.completed || 0}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px', textAlign: 'center' }}>
                                            {user.modules || 0}
                                        </td>
                                        <td style={{ padding: '10px', textAlign: 'center' }}>
                                            <div style={{
                                                display: 'inline-block',
                                                width: '60px',
                                                height: '6px',
                                                background: '#e0e0e0',
                                                borderRadius: '3px',
                                                marginRight: '8px',
                                                overflow: 'hidden'
                                            }}>
                                                <div style={{
                                                    width: `${Math.min(rate, 100)}%`,
                                                    height: '100%',
                                                    background: getCompletionColor(rate),
                                                    borderRadius: '3px',
                                                    transition: 'width 0.5s ease'
                                                }} />
                                            </div>
                                            <span style={{ fontWeight: 'bold' }}>{rate}%</span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr style={{
                                backgroundColor: '#003152',
                                color: 'white',
                                fontWeight: 'bold'
                            }}>
                                <td style={{ padding: '12px' }}>📊 TOTAL</td>
                                <td style={{ padding: '12px', textAlign: 'center' }}>{totalRow.assigned}</td>
                                <td style={{ padding: '12px', textAlign: 'center' }}>{totalRow.completed}</td>
                                <td style={{ padding: '12px', textAlign: 'center' }}>{totalRow.modules}</td>
                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                    {totalRow.assigned > 0
                                        ? Math.round((totalRow.completed / totalRow.assigned) * 100) + '%'
                                        : '0%'
                                    }
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                ) : (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px',
                        color: '#666',
                        fontSize: '16px'
                    }}>
                        No New users found for this month
                    </div>
                )}
            </div>

            {/* Close Button */}
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <RaisedButton
                    label="Close"
                    onClick={onClose}
                    primary={true}
                />
            </div>
        </Dialog>
    );
};

export default PopupModal;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/reusableComponents/StackedBarChart/PopupModal.tsx